import { LGraphNode, LiteGraph } from "litegraph.js";

/**
 * MouseNode
 * ---------
 * Outputs mouse position (normalized 0–1), velocity, click state,
 * and distance from center. Wire to any parameter for interactive control.
 */
export class MouseNode extends LGraphNode {
  constructor() {
    super();
    this.title = "Mouse";

    this.properties = {
      active: true,
      smooth: 0.15,
    };

    this.addWidget("toggle", "Active", true, (val) => {
      this.properties.active = val;
    });

    this._rawX = 0.5;
    this._rawY = 0.5;
    this._smoothX = 0.5;
    this._smoothY = 0.5;
    this._prevX = 0.5;
    this._prevY = 0.5;
    this._velX = 0;
    this._velY = 0;
    this._pressed = false;
    this._speed = 0;

    this.addOutput("X", "number");
    this.addOutput("Y", "number");
    this.addOutput("Speed", "number");
    this.addOutput("Vel X", "number");
    this.addOutput("Vel Y", "number");
    this.addOutput("Pressed", "number");
    this.addOutput("Distance", "number");

    this.addWidget("slider", "Smooth", 0.15, (val) => {
      this.properties.smooth = val;
    }, { min: 0, max: 0.5, step: 0.01 });

    this._onMouseMove = (e) => {
      if (!this.properties.active) return;
      this._rawX = e.clientX / window.innerWidth;
      this._rawY = e.clientY / window.innerHeight;
    };
    this._onMouseDown = () => { if (this.properties.active) this._pressed = true; };
    this._onMouseUp = () => { if (this.properties.active) this._pressed = false; };

    window.addEventListener("mousemove", this._onMouseMove);
    window.addEventListener("mousedown", this._onMouseDown);
    window.addEventListener("mouseup", this._onMouseUp);

    this.size = [160, 200];
    this.mode = LiteGraph.ALWAYS;
    this.color = "#2a2a2a";
    this.bgcolor = "#333333";
  }

  onDrawForeground(ctx) {
    const w = this.size[0];
    const h = this.size[1];

    // Crosshair visualization
    const vizY = 30;
    const vizSize = Math.min(w - 20, 50);
    const vizX = (w - vizSize) / 2;

    // Background
    ctx.fillStyle = "rgba(0,0,0,0.25)";
    ctx.fillRect(vizX, vizY, vizSize, vizSize);

    // Grid lines
    ctx.strokeStyle = "rgba(255,255,255,0.1)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(vizX + vizSize / 2, vizY);
    ctx.lineTo(vizX + vizSize / 2, vizY + vizSize);
    ctx.moveTo(vizX, vizY + vizSize / 2);
    ctx.lineTo(vizX + vizSize, vizY + vizSize / 2);
    ctx.stroke();

    // Cursor dot
    const dotX = vizX + this._smoothX * vizSize;
    const dotY = vizY + this._smoothY * vizSize;
    ctx.fillStyle = this._pressed ? "#ff6b6b" : "#4E9EFF";
    ctx.beginPath();
    ctx.arc(dotX, dotY, this._pressed ? 5 : 4, 0, Math.PI * 2);
    ctx.fill();

    // Speed bar
    const barY = vizY + vizSize + 6;
    const barW = w - 20;
    ctx.fillStyle = "rgba(0,0,0,0.25)";
    ctx.fillRect(10, barY, barW, 4);
    ctx.fillStyle = "rgba(78,158,255,0.6)";
    ctx.fillRect(10, barY, barW * Math.min(this._speed, 1), 4);

    // Labels
    ctx.fillStyle = "rgba(255,255,255,0.4)";
    ctx.font = "9px monospace";
    ctx.fillText(`X:${this._smoothX.toFixed(2)} Y:${this._smoothY.toFixed(2)}`, 10, barY + 14);
    ctx.fillText(`spd:${this._speed.toFixed(3)}`, 10, barY + 24);
  }

  onExecute() {
    const s = 1 - this.properties.smooth;

    // Smooth interpolation
    this._smoothX += (this._rawX - this._smoothX) * s;
    this._smoothY += (this._rawY - this._smoothY) * s;

    // Velocity
    this._velX = this._smoothX - this._prevX;
    this._velY = this._smoothY - this._prevY;
    this._speed = Math.sqrt(this._velX * this._velX + this._velY * this._velY);

    this._prevX = this._smoothX;
    this._prevY = this._smoothY;

    // Distance from center (0 at center, 1 at corners)
    const dx = this._smoothX - 0.5;
    const dy = this._smoothY - 0.5;
    const dist = Math.sqrt(dx * dx + dy * dy) * 2; // normalize so corner ≈ 1

    this.setOutputData(0, this._smoothX);
    this.setOutputData(1, this._smoothY);
    this.setOutputData(2, this._speed);
    this.setOutputData(3, this._velX);
    this.setOutputData(4, this._velY);
    this.setOutputData(5, this._pressed ? 1 : 0);
    this.setOutputData(6, dist);
    this.setDirtyCanvas(true);
  }

  onRemoved() {
    window.removeEventListener("mousemove", this._onMouseMove);
    window.removeEventListener("mousedown", this._onMouseDown);
    window.removeEventListener("mouseup", this._onMouseUp);
  }
}

LiteGraph.registerNodeType("input/Mouse", MouseNode);
